import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PrefetchLink from '@/components/PrefetchLink';
import { calcHeadcount } from '@/lib/calcHeadcount';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import AIRecommendations from '@/components/AIRecommendations';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, Users, Edit, Trash2, RotateCcw, Clock } from 'lucide-react';
import MembershipCountdownChip from '@/components/MembershipCountdownChip';
import EnableGuestsBanner from '@/components/EnableGuestsBanner';

const Dashboard = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState({ hosting: [], attending: [], past: [] });
  const [deletedEvents, setDeletedEvents] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyEvents();
    fetchDeletedEvents();
  }, []);

  const fetchMyEvents = async () => {
    try {
      const response = await axios.get(`${API}/users/me/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchDeletedEvents = async () => {
    try {
      const res = await axios.get(`${API}/events/my-events/deleted`);
      setDeletedEvents(res.data || []);
    } catch (error) {
      console.error('Failed to fetch deleted events:', error);
    }
  };
  
  const handleRestore = async (eventId, title) => {
    setRestoringId(eventId);
    try {
      await axios.post(`${API}/events/${eventId}/restore`);
      toast.success(`"${title}" restored successfully`);
      // Refresh both lists
      await Promise.all([fetchMyEvents(), fetchDeletedEvents()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore event');
    } finally {
      setRestoringId(null);
    }
  };

  // Total headcount is now provided by the shared `useHeadcount` helper so
  // future tweaks (e.g. including waitlist) only need editing in one place.
  const headcount = calcHeadcount;

  // Event card for events user is attending (no edit button)
  const EventCard = ({ event }) => (
    <PrefetchLink to={`/events/${event.event_id}`} eventId={event.event_id}>
      <div className="bg-white rounded-3xl border-2 border-border overflow-hidden hover:shadow-xl transition-all duration-300 group">
        <div className="overflow-hidden h-48">
          <img
            src={
              event.image_url ||
              'https://images.unsplash.com/photo-1719508784075-a49e1dcc0c0e?crop=entropy&cs=srgb&fm=jpg&q=85'
            }
            alt={event.title}
            className="event-card-image w-full h-full object-contain bg-black/5"
          />
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3 py-1 bg-accent text-accent-foreground text-xs font-bold rounded-full">
              {event.group_name || event.category}
            </span>
          </div>
          <h3 className="text-xl font-heading font-bold mb-2 line-clamp-2">{event.title}</h3>
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{event.description}</p>
          <div className="space-y-2 text-sm">
            <div className="flex items-center text-muted-foreground">
              <MapPin className="w-4 h-4 mr-2" />
              {event.city}, {event.country}
            </div>
            <div className="flex items-center text-muted-foreground">
              <Calendar className="w-4 h-4 mr-2" />
              {event.event_date} at {event.event_time}
            </div>
            <div className="flex items-center text-muted-foreground">
              <Users className="w-4 h-4 mr-2" />
              {headcount(event)} attending
            </div>
          </div>
        </div>
      </div>
    </PrefetchLink>
  );

  // Event card for events user is hosting (with edit button)
  const HostingEventCard = ({ event }) => (
    <div className="bg-white rounded-3xl border-2 border-border overflow-hidden hover:shadow-xl transition-all duration-300 group">
      <PrefetchLink to={`/events/${event.event_id}`} eventId={event.event_id}>
        <div className="overflow-hidden h-48 relative">
          <img
            src={
              event.image_url ||
              'https://images.unsplash.com/photo-1719508784075-a49e1dcc0c0e?crop=entropy&cs=srgb&fm=jpg&q=85'
            }
            alt={event.title}
            className="event-card-image w-full h-full object-contain bg-black/5"
          />
          <div className="absolute top-3 left-3">
            <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full">
              Hosting
            </span>
          </div>
        </div>
      </PrefetchLink>
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="px-3 py-1 bg-accent text-accent-foreground text-xs font-bold rounded-full">
            {event.group_name || event.category}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/edit-event/${event.event_id}`);
            }}
            data-testid={`edit-event-${event.event_id}`}
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>
        <Link to={`/events/${event.event_id}`}>
          <h3 className="text-xl font-heading font-bold mb-2 line-clamp-2 hover:text-primary transition-colors">{event.title}</h3>
        </Link>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{event.description}</p>
        <div className="space-y-2 text-sm">
          <div className="flex items-center text-muted-foreground">
            <MapPin className="w-4 h-4 mr-2" />
            {event.city}, {event.country}
          </div>
          <div className="flex items-center text-muted-foreground">
            <Calendar className="w-4 h-4 mr-2" />
            {event.event_date} at {event.event_time}
          </div>
          <div className="flex items-center text-muted-foreground">
            <Users className="w-4 h-4 mr-2" />
            {headcount(event)} attending
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-8">
              <div className="mb-3">
                <MembershipCountdownChip />
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">My Events</h1>
              <p className="text-lg text-muted-foreground">Manage your hosted and attending events</p>
            </div>

            <Tabs defaultValue="hosting" className="w-full">
              <TabsList className="mb-8 bg-white border-2 border-border p-1 h-14 rounded-full">
                <TabsTrigger
                  value="hosting"
                  className="rounded-full px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold"
                  data-testid="tab-hosting"
                >
                  Hosting ({events.hosting.length})
                </TabsTrigger>
                <TabsTrigger
                  value="attending"
                  className="rounded-full px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold"
                  data-testid="tab-attending"
                >
                  Attending ({events.attending.length})
                </TabsTrigger>
                <TabsTrigger
                  value="past"
                  className="rounded-full px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold"
                  data-testid="tab-past"
                >
                  Past ({events.past.length})
                </TabsTrigger>
                <TabsTrigger
                  value="deleted"
                  className="rounded-full px-6 data-[state=active]:bg-red-500 data-[state=active]:text-white font-bold"
                  data-testid="tab-deleted"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Recently Deleted ({deletedEvents.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="hosting">
                <EnableGuestsBanner />
                {loading ? (
                  <div className="text-center py-20">
                    <div className="text-lg text-muted-foreground">Loading...</div>
                  </div>
                ) : events.hosting.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="text-lg text-muted-foreground mb-4">
                      You haven't created any events yet
                    </div>
                    <Link
                      to="/create-event"
                      className="text-primary hover:underline font-semibold"
                      data-testid="create-first-event-link"
                    >
                      Create your first event
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {events.hosting.map((event, index) => (
                      <motion.div
                        key={event.event_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        data-testid={`hosting-event-${index}`}
                      >
                        <HostingEventCard event={event} />
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="attending">
                {loading ? (
                  <div className="text-center py-20">
                    <div className="text-lg text-muted-foreground">Loading...</div>
                  </div>
                ) : events.attending.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="text-lg text-muted-foreground mb-4">
                      You're not attending any events yet
                    </div>
                    <Link
                      to="/events"
                      className="text-primary hover:underline font-semibold"
                      data-testid="browse-events-link"
                    >
                      Browse available events
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {events.attending.map((event, index) => (
                      <motion.div
                        key={event.event_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        data-testid={`attending-event-${index}`}
                      >
                        <EventCard event={event} />
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="past">
                {loading ? (
                  <div className="text-center py-20">
                    <div className="text-lg text-muted-foreground">Loading...</div>
                  </div>
                ) : events.past.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="text-lg text-muted-foreground mb-4">
                      No past events yet
                    </div>
                    <Link
                      to="/events"
                      className="text-primary hover:underline font-semibold"
                    >
                      Discover upcoming events
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {events.past.map((event, index) => (
                      <motion.div
                        key={event.event_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        data-testid={`past-event-${index}`}
                      >
                        <EventCard event={event} />
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="deleted">
                {deletedEvents.length === 0 ? (
                  <div className="text-center py-20">
                    <Trash2 className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                    <div className="text-lg text-muted-foreground mb-2">
                      No recently deleted events
                    </div>
                    <p className="text-sm text-muted-foreground/80">
                      Events you delete will appear here for 30 days before being permanently removed.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3" data-testid="deleted-events-notice">
                      <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                      <div className="text-sm text-amber-900">
                        <p className="font-semibold mb-0.5">Events are kept for 30 days</p>
                        <p className="text-amber-800/90">After that, they're permanently removed and cannot be recovered.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {deletedEvents.map((event, index) => (
                        <motion.div
                          key={event.event_id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          data-testid={`deleted-event-${index}`}
                          className="bg-white rounded-3xl border-2 border-red-100 overflow-hidden hover:shadow-xl transition-all opacity-75 hover:opacity-100"
                        >
                          <div className="overflow-hidden h-40 relative">
                            <img
                              src={event.image_url || 'https://images.unsplash.com/photo-1719508784075-a49e1dcc0c0e?crop=entropy&cs=srgb&fm=jpg&q=85'}
                              alt={event.title}
                              className="w-full h-full object-contain bg-black/5 grayscale"
                            />
                            <div className="absolute top-3 left-3 px-2.5 py-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                              <Trash2 className="w-3 h-3" />
                              Deleted
                            </div>
                          </div>
                          <div className="p-5">
                            <h3 className="text-lg font-heading font-bold mb-2 line-clamp-2" data-testid={`deleted-event-title-${index}`}>{event.title}</h3>
                            <div className="text-sm text-muted-foreground space-y-1.5 mb-4">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(event.event_date).toLocaleDateString()} {event.event_time}
                              </div>
                              {event.city && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {event.city}, {event.country}
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-amber-700 font-medium">
                                <Clock className="w-3.5 h-3.5" />
                                {event.days_left ?? 0} days left to restore
                              </div>
                            </div>
                            <Button
                              onClick={() => handleRestore(event.event_id, event.title)}
                              disabled={restoringId === event.event_id}
                              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                              data-testid={`restore-event-${index}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              {restoringId === event.event_id ? 'Restoring...' : 'Restore Event'}
                            </Button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>

            {/* AI Recommendations */}
            <div className="mt-12">
              <AIRecommendations />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;